import type { Delegate, RequestOf } from "../../access/types.ts";
import { Access } from "../../access/types.ts";
import type { Maturity } from "../shared.ts";

export const ACCESS_TYPES = {
  group: "group",
  role: "role",
} as const;

type AccessType = (typeof ACCESS_TYPES)[keyof typeof ACCESS_TYPES];

type BaseAccess<T extends AccessType> = {
  readonly description: string;
  readonly label: string;
  readonly maturity: Maturity;
  readonly generated: Record<string, never>;
  readonly delegation: Record<string, Delegate<any, any>>;
  readonly parameters: {
    readonly name: {
      readonly type: "dynamic";
      readonly label: string;
      readonly description: string;
    };
  } & (T extends typeof ACCESS_TYPES.group
    ? {
        readonly group: {
          readonly type: "generated";
          readonly label: string;
          readonly description: string;
        };
      }
    : {
        readonly role: {
          readonly type: "generated";
          readonly label: string;
          readonly description: string;
        };
      });
  readonly render: { policy: "name" };
};

type MembershipAccess = BaseAccess<typeof ACCESS_TYPES.group>;
type AssignmentAccess = BaseAccess<typeof ACCESS_TYPES.role>;

const newMembershipAccess = (maturity: Maturity): MembershipAccess => {
  return Access({
    description: "Add user to group",
    label: "Group membership",
    maturity,
    generated: {},
    delegation: {},
    parameters: {
      name: {
        type: "dynamic",
        label: "Group name",
        description: "Group name",
      },
      group: {
        type: "generated",
        label: "Group",
        description: "Group ID",
      },
    },
    render: { policy: "name" } as const,
  });
};

const newRoleAssignmentAccess = (maturity: Maturity): AssignmentAccess => {
  return Access({
    description: "Assign role to user",
    label: "Role assignment",
    maturity,
    generated: {},
    delegation: {},
    parameters: {
      name: {
        type: "dynamic",
        label: "Role name",
        description: "Role name",
      },
      role: {
        type: "generated",
        label: "Role",
        description: "Role ID",
      },
    },
    render: { policy: "name" } as const,
  });
};

export type DirectoryGroupAccesses = {
  membership: MembershipAccess;
};

export const newDirectoryGroupAccesses = (
  maturity: Maturity
): DirectoryGroupAccesses => ({
  membership: newMembershipAccess(maturity),
});

export type DirectoryGroupAndRoleAccesses = {
  membership: MembershipAccess;
  assignment: AssignmentAccess;
};

export const newDirectoryGroupAndRoleAccesses = (
  maturity: Maturity
): DirectoryGroupAndRoleAccesses => ({
  membership: newMembershipAccess(maturity),
  assignment: newRoleAssignmentAccess(maturity),
});

export type DirectoryAccesses =
  | DirectoryGroupAccesses
  | DirectoryGroupAndRoleAccesses;

export type AssignmentType = "groupAndRole" | "groupOnly";

export const isDirectoryAccessesWithAssignment = <A extends DirectoryAccesses>(
  accesses: A
): accesses is A & DirectoryGroupAndRoleAccesses => {
  return "assignment" in accesses;
};

export type AllDirectoryAccesses = DirectoryGroupAccesses &
  DirectoryGroupAndRoleAccesses;

export type DirectoryMembershipAccess = AllDirectoryAccesses["membership"];
export type DirectoryMembershipRequest = RequestOf<DirectoryMembershipAccess>;

export type DirectoryRoleAssignmentAccess = AllDirectoryAccesses["assignment"];
